import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './NotificationBell.css';

/**
 * The notification bell, used by both the district admin and parent dashboards.
 *
 * In-app only, no email - see backend/services/notifications.js for why.
 *
 * For district staff these are shared: one read state for the whole office, the same model
 * as the processed mark on a printed form. So "read" here means "somebody in the office has
 * seen it", which is the useful meaning when two people work from one screen.
 *
 * If the count cannot be loaded the bell renders with no badge rather than an error: a
 * failed count must not break the header on every screen. Opening the panel does report the
 * failure, because at that point the user is asking for the content.
 */

const POLL_MS = 120000; // Quiet polling. These are day-scale events, not chat messages.

const NotificationBell = ({ onNavigate }) => {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const panelRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const res = await axios.get('/api/notifications/unread-count');
      setUnread(res.data.unread || 0);
    } catch (error) {
      // Deliberately silent: see the component note above.
    }
  }, []);

  useEffect(() => {
    loadCount();
    const timer = setInterval(loadCount, POLL_MS);
    return () => clearInterval(timer);
  }, [loadCount]);

  // Close on an outside click or Escape, so the panel does not trap the page.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openPanel = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setListLoading(true);
    setListError('');
    try {
      const res = await axios.get('/api/notifications', { params: { limit: 30 } });
      setItems(res.data.notifications || []);
    } catch (error) {
      setListError(error.response?.data?.reason || error.response?.data?.error || error.message);
    } finally {
      setListLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await axios.post(`/api/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
      loadCount();
    } catch (error) {
      setListError(error.response?.data?.error || error.message);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post('/api/notifications/read-all');
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
      setUnread(0);
    } catch (error) {
      setListError(error.response?.data?.error || error.message);
    }
  };

  const activate = (n) => {
    if (!n.read_at) markRead(n.id);
    if (n.link && onNavigate) {
      setOpen(false);
      onNavigate(n.link);
    }
  };

  return (
    <div className="notif" ref={panelRef}>
      <button
        type="button"
        className="notif-bell"
        onClick={openPanel}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-panel-head">
            <strong>Notifications</strong>
            {items.some((n) => !n.read_at) && (
              <button type="button" className="notif-linkish" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          {listLoading && <p className="notif-empty">Loading…</p>}
          {listError && <p className="notif-error">{listError}</p>}
          {!listLoading && !listError && items.length === 0 && (
            <p className="notif-empty">Nothing new.</p>
          )}

          <ul className="notif-list">
            {items.map((n) => (
              <li key={n.id} className={n.read_at ? 'notif-item read' : 'notif-item'}>
                <button type="button" className="notif-item-btn" onClick={() => activate(n)}>
                  <span className="notif-title">{n.title}</span>
                  {n.body && <span className="notif-body">{n.body}</span>}
                  <span className="notif-meta">
                    {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                    {n.created_by_name ? ` · ${n.created_by_name}` : ''}
                    {n.read_at && n.read_by_name ? ` · read by ${n.read_by_name}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
